import { createRouter, createWebHistory } from 'vue-router';

import { useAuthStore } from '../stores/auth';
import { useBonusShopStore } from '../stores/bonusShop';

/** New deep links: build URLs via task-route / company-route helpers — never hand-assemble paths. */
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('../pages/LoginPage.vue'),
      meta: { public: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../pages/LoginPage.vue'),
      meta: { public: true },
    },
    {
      path: '/verify',
      name: 'verify',
      component: () => import('../pages/VerifyPage.vue'),
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../pages/ForgotPasswordPage.vue'),
      meta: { public: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../pages/ResetPasswordPage.vue'),
      meta: { public: true },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../pages/DashboardPage.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      name: 'home',
      component: () => import('../pages/HomePage.vue'),
    },
    {
      path: '/:companyId(\\d+)/:projectId(\\d+)/:taskId(\\d+)',
      name: 'task-details',
      component: () => import('../pages/TaskDetailsPage.vue'),
    },
    {
      path: '/:companyId(\\d+)/:projectId(\\d+)',
      name: 'company-projects-section',
      component: () => import('../pages/CompanyProjectsSectionPage.vue'),
    },
    {
      path: '/:companyId(\\d+)',
      name: 'company-details',
      component: () => import('../pages/CompanyDetailsPage.vue'),
    },
    {
      path: '/tasks/:id',
      // Legacy deep link — prefer task-route helpers for new URLs.
      name: 'task-details-legacy',
      component: () => import('../pages/TaskDetailsPage.vue'),
    },
    {
      path: '/book/cancelled',
      name: 'book-cancel',
      component: () => import('../pages/BookingCancelPage.vue'),
      meta: { public: true },
    },
    {
      path: '/book/:slug',
      name: 'book-public',
      component: () => import('../pages/BookingPublicPage.vue'),
      meta: { public: true },
    },
    {
      path: '/booking',
      name: 'booking-availability',
      component: () => import('../pages/BookingAvailabilityPage.vue'),
    },
    {
      path: '/archive',
      name: 'archive',
      component: () => import('../pages/ArchivePage.vue'),
    },
    {
      path: '/calendar',
      name: 'calendar',
      component: () => import('../pages/CalendarPage.vue'),
    },
    {
      path: '/pile',
      name: 'pile',
      component: () => import('../pages/PilePage.vue'),
    },
    {
      path: '/my-stuff',
      redirect: '/',
    },
    {
      path: '/companies',
      name: 'companies',
      component: () => import('../pages/CompaniesPage.vue'),
    },
    {
      path: '/companies/:id',
      // Legacy company URL — prefer company-route helpers for new links.
      name: 'company-details-legacy',
      component: () => import('../pages/CompanyDetailsPage.vue'),
    },
    {
      path: '/companies/:id/projects',
      // Legacy company projects URL — prefer company-route helpers for new links.
      name: 'company-projects-section-legacy',
      component: () => import('../pages/CompanyProjectsSectionPage.vue'),
    },
    {
      path: '/companies/:id/notes',
      name: 'company-notes-section',
      component: () => import('../pages/CompanyNotesSectionPage.vue'),
    },
    {
      path: '/companies/:id/notes/:noteId',
      name: 'company-note-details',
      component: () => import('../pages/CompanyNoteDetailsPage.vue'),
    },
    {
      path: '/companies/:id/check-ins/:ruleId',
      name: 'company-checkin-details',
      component: () => import('../pages/CompanyCheckinDetailsPage.vue'),
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('../pages/ReportsPage.vue'),
    },
    {
      path: '/adminland',
      name: 'adminland',
      component: () => import('../pages/AdminlandPage.vue'),
      meta: { adminOnly: true },
    },
    {
      path: '/management',
      name: 'management-dashboard',
      component: () => import('../pages/ManagementPage.vue'),
      meta: { adminOnly: true },
    },
    {
      path: '/management/legal',
      name: 'management-legal',
      component: () => import('../pages/ManagementPage.vue'),
      meta: { adminOnly: true },
    },
    {
      path: '/management/finance',
      name: 'management-finance',
      component: () => import('../pages/ManagementPage.vue'),
      meta: { adminOnly: true },
    },
    {
      path: '/management/:unknown(.*)',
      redirect: { name: 'management-dashboard' },
    },
    {
      path: '/settings',
      redirect: { name: 'platform' },
    },
    {
      path: '/platform',
      name: 'platform',
      component: () => import('../pages/PlatformPage.vue'),
      meta: { superadminOnly: true },
    },
    {
      path: '/superadmin',
      redirect: { name: 'platform' },
    },
    {
      path: '/rewards',
      name: 'rewards',
      component: () => import('../pages/RewardsPage.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const bonusShop = useBonusShopStore();
  if (auth.initializing) {
    await auth.refresh();
  }
  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'home' };
  }
  if (to.name === 'register' && auth.isAuthenticated) {
    return { name: 'home' };
  }
  if (to.name === 'verify' && auth.isAuthenticated && auth.user?.emailVerified) {
    return { name: 'home' };
  }
  if (
    to.meta.adminOnly &&
    (auth.user?.membershipType === 'company' || !auth.user?.isAdmin)
  ) {
    return { name: 'home' };
  }
  if (to.meta.superadminOnly && !auth.user?.isSuperadmin) {
    return { name: 'home' };
  }
  if (to.name === 'rewards') {
    if (!bonusShop.programLoaded) {
      await bonusShop.loadProgram();
    }
    if (!bonusShop.gamificationEnabled || !bonusShop.programEnabled) {
      return { name: 'home' };
    }
  }
  if (
    to.name === 'companies' &&
    auth.user?.membershipType === 'company' &&
    auth.user.companyId
  ) {
    return {
      name: 'company-details-legacy',
      params: { id: auth.user.companyId },
    };
  }
  return true;
});
